import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Users, Store, Car as CarIcon, Calendar as CalendarIcon, Search, X,
         Settings, ListChecks, CalendarRange, Wallet, UserCog, Globe, FileText as FileTextIcon, Wrench,
         ChevronDown, ChevronUp, Menu as MenuIcon } from "lucide-react";
import BookingsCalendar from "@/components/calendar/BookingsCalendar";
import TodayBookingFilters, { applyTodayFilter } from "@/components/bookings/TodayBookingFilters";
import PendingApprovalsPanel from "@/components/bookings/PendingApprovalsPanel";
import VendorBookingsList from "@/components/vendor/VendorBookingsList";
import AdminOpsPanel from "@/components/admin/AdminOpsPanel";
import ChangePasswordCard from "@/components/profile/ChangePasswordCard";
import AdminCredentialsPanel from "@/components/admin/AdminCredentialsPanel";
import AdminVendorFilters, { EMPTY_VENDOR_FILTERS, applyVendorFilters } from "@/components/admin/AdminVendorFilters";
import AdminRentalCarImportDialog from "@/components/admin/AdminRentalCarImportDialog";
import AdminTeamPanel from "@/components/admin/AdminTeamPanel";
import ManualBookingDialog from "@/components/booking/ManualBookingDialog";
import VendorCommissionDialog, { commissionLabel } from "@/components/admin/VendorCommissionDialog";
import VendorAccessTrigger from "@/components/admin/VendorAccessTrigger";
import AdminVendorDocumentsDialog from "@/components/admin/AdminVendorDocumentsDialog";
import AdminBillingPanel from "@/components/admin/AdminBillingPanel";
import AdminPlatformInvoicesPanel from "@/components/admin/AdminPlatformInvoicesPanel";
import AdminSiteSettingsPanel from "@/components/admin/AdminSiteSettingsPanel";
import AdminIntegrationCredentialsPanel from "@/components/admin/AdminIntegrationCredentialsPanel";
import AdminChatAnalyticsTile from "@/components/admin/AdminChatAnalyticsTile";
import AdminChatLeadsTile from "@/components/admin/AdminChatLeadsTile";
import GooglePlacesHealthBanner from "@/components/admin/GooglePlacesHealthBanner";
import AdminLiveChatPopup from "@/components/admin/AdminLiveChatPopup";
import AdminFaqsPanel from "@/components/admin/AdminFaqsPanel";
import AdminSearchConsoleHealthTile from "@/components/admin/AdminSearchConsoleHealthTile";
import AdminPricingPlayground from "@/components/admin/AdminPricingPlayground";
import AdminFxPanel from "@/components/admin/AdminFxPanel";
import AdminFeaturedPanel from "@/components/admin/AdminFeaturedPanel";
import AISettingsPanel from "@/components/admin/AISettingsPanel";
import AdminFinancePanel from "@/components/admin/AdminFinancePanel";
import OpsSummaryCards from "@/components/ops/OpsSummaryCards";
import OpsBottomNav from "@/components/ops/OpsBottomNav";
import QuickActionsFab from "@/components/ops/QuickActionsFab";
import MobileBookingSearch from "@/components/ops/MobileBookingSearch";
import { CalendarPlus, CreditCard, CalendarOff, Zap, ExternalLink } from "lucide-react";
import { impersonate } from "@/components/AdminImpersonationBanner";
import AdminImpersonationLogPanel from "@/components/admin/AdminImpersonationLogPanel";
import { useNavigate } from "react-router-dom";

// Iter 65: Admin dashboard restructured into a clear top-level tab nav requested by
// the operator: Platform Operations · Bookings (All + Live Calendar) · Billing ·
// Vendors · My Account. Persist the active tab in the URL hash so refresh / shared
// links land on the same place.
const VALID_TABS = ["operations", "bookings", "billing", "vendors", "site", "tools", "account"];

// Iter 69 — labels surfaced on the collapsible header so the admin sees
// which section is currently open even when the menu is collapsed.
const _ADMIN_TAB_LABELS = {
  operations: "Platform Operations",
  bookings: "Bookings",
  billing: "Billing",
  vendors: "Vendors",
  site: "Site Settings",
  tools: "Tools",
  account: "My Account",
};

function _readTabFromHash() {
  const h = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#/, "");
  return VALID_TABS.includes(h) ? h : "operations";
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [bookings, setBookings] = useState([]);
  // Iter 76 — quick day filter (today / pickups / returns / overdue), Albania time.
  const [todayFilter, setTodayFilter] = useState(null);
  const [query, setQuery] = useState("");
  // Iter 68 — advanced vendor filters (8 facets). Lives at the page level so
  // the same filtered subset feeds both the desktop table and the mobile cards.
  const [vendorFilters, setVendorFilters] = useState({ ...EMPTY_VENDOR_FILTERS });
  const [commissionTarget, setCommissionTarget] = useState(null);
  const [docsTarget, setDocsTarget] = useState(null);
  const [attentionCounts, setAttentionCounts] = useState({ pending_vendors: 0, pending_documents: 0, new_signups_24h: 0, total: 0 });
  const [tab, setTab] = useState(_readTabFromHash);
  const navigate = useNavigate();
  // Iter 96 — controlled bookings subtab so KPI cards / bottom nav can deep-link to the calendar.
  const [bookingsSubtab, setBookingsSubtab] = useState("list");
  // Iter 69 — Top, collapsible menu. Default collapsed so the dashboard
  // opens with the stats/content getting maximum vertical space. Persists
  // across reloads via localStorage so the admin's preference sticks.
  const [tabsCollapsed, setTabsCollapsed] = useState(() => {
    try { return localStorage.getItem("rentalcar_admin_tabs_collapsed") !== "0"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("rentalcar_admin_tabs_collapsed", tabsCollapsed ? "1" : "0"); }
    catch { /* private mode */ }
  }, [tabsCollapsed]);

  const loadBookings = () => api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});

  const load = async () => {
    try {
      const [s, v, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/vendors"),
        api.get("/admin/vendor-attention-counts").catch(() => ({ data: null })),
      ]);
      setStats(s.data); setVendors(v.data);
      if (c.data) setAttentionCounts(c.data);
    } catch (e) {
      console.error("admin dashboard load failed:", e);
    }
    loadBookings();
  };

  useEffect(() => { load(); }, []);

  // Iter 67cb — quiet poll of the attention counts every 60s so a long-lived
  // admin dashboard stays accurate without a full reload. Stops polling when
  // the tab is hidden to avoid wasted network calls.
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      api.get("/admin/vendor-attention-counts")
        .then((r) => { if (alive && r.data) setAttentionCounts(r.data); })
        .catch(() => {});
    };
    const id = setInterval(refresh, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Persist the active tab in the URL hash + react to back/forward navigation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.replace(/^#/, "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);
  useEffect(() => {
    const onHash = () => setTab(_readTabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const approve = async (id) => { await api.post(`/admin/vendors/${id}/approve`); toast.success("Vendor approved"); load(); };
  const reject = async (id) => { await api.post(`/admin/vendors/${id}/reject`); toast.success("Vendor rejected"); load(); };
  const toggleManual = async (v) => {
    try {
      await api.patch(`/admin/vendors/${v.id}/manual-bookings`, { enabled: !v.manual_booking_enabled });
      toast.success(v.manual_booking_enabled ? "Manual bookings disabled" : "Manual bookings enabled");
      load();
    } catch (e) { toast.error("Could not toggle"); }
  };

  const q = query.trim().toLowerCase();

  const sortedBookings = useMemo(() => {
    const arr = [...bookings];
    arr.sort((a, b) => {
      const aT = new Date(a.created_at || a.start_date || 0).getTime();
      const bT = new Date(b.created_at || b.start_date || 0).getTime();
      return bT - aT;
    });
    return arr;
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    if (!q) return sortedBookings;
    return sortedBookings.filter((b) => {
      const code = String(b.id || "").toLowerCase();
      const make = String(b.car?.make || "").toLowerCase();
      const model = String(b.car?.model || "").toLowerCase();
      const customer = String(b.customer?.name || "").toLowerCase();
      const customerEmail = String(b.customer?.email || "").toLowerCase();
      const vendorName = String(b.vendor?.business_name || b.vendor?.name || "").toLowerCase();
      const location = String(b.pickup_location || "").toLowerCase();
      return (
        code.startsWith(q) ||
        code.slice(0, 8).includes(q) ||
        make.includes(q) ||
        model.includes(q) ||
        `${make} ${model}`.includes(q) ||
        customer.includes(q) ||
        customerEmail.includes(q) ||
        vendorName.includes(q) ||
        location.includes(q)
      );
    });
  }, [sortedBookings, q]);

  const filteredVendors = useMemo(() => {
    // Iter 68 — 2-stage filter: facet filters first (cheap structural check),
    // then the free-text search across name / email / business / phone.
    const facetFiltered = applyVendorFilters(vendors, vendorFilters);
    if (!q) return facetFiltered;
    return facetFiltered.filter((v) => {
      const name = String(v.name || "").toLowerCase();
      const email = String(v.email || "").toLowerCase();
      const business = String(v.business_name || "").toLowerCase();
      const phone = String(v.phone || "").toLowerCase();
      return name.includes(q) || email.includes(q) || business.includes(q) || phone.includes(q);
    });
  }, [vendors, q, vendorFilters]);

  const hasActiveVendorFilter = useMemo(
    () => Object.values(vendorFilters).some((v) => v !== "all"),
    [vendorFilters],
  );

  const hasQuery = q.length > 0;

  const displayedBookings = useMemo(
    () => applyTodayFilter(filteredBookings, todayFilter),
    [filteredBookings, todayFilter],
  );

  // Iter 96 — clickable KPI cards + mobile bottom nav navigation.
  const handleSummaryNav = (key) => {
    if (["today", "pickups", "returns", "overdue"].includes(key)) {
      setTab("bookings"); setBookingsSubtab("list"); setTodayFilter(key);
    } else if (["active", "pending", "awaiting_payment"].includes(key)) {
      setTab("bookings"); setBookingsSubtab("list"); setTodayFilter(null);
    } else if (key === "vehicles") {
      setTab("bookings"); setBookingsSubtab("calendar");
    } else if (key === "revenue") {
      setTab("billing");
    }
  };

  const handleBottomNav = (key) => {
    if (key === "today-ops") { navigate("/dashboard/today"); return; }
    if (key === "agreements") { navigate("/dashboard/agreements"); return; }
    if (key === "calendar") { setTab("bookings"); setBookingsSubtab("calendar"); }
    else if (key === "bookings") { setTab("bookings"); setBookingsSubtab("list"); }
    else setTab(key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const bottomNavActive = tab === "bookings" ? (bookingsSubtab === "calendar" ? "calendar" : "bookings") : tab;

  const fabActions = [
    { key: "today-ops", label: "Today Operations", icon: Zap, onClick: () => navigate("/dashboard/today") },
    { key: "new-reservation", label: "New Reservation", icon: CalendarPlus, onClick: () => handleBottomNav("bookings") },
    { key: "block-vehicle", label: "Block Vehicle Dates", icon: CalendarOff, onClick: () => handleBottomNav("calendar") },
    { key: "vendors", label: "Manage Vendors", icon: Store, onClick: () => handleBottomNav("vendors") },
    { key: "record-payment", label: "Billing & Payments", icon: CreditCard, onClick: () => handleBottomNav("billing") },
  ];

  return (
    <div>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between gap-3 mb-6">
          <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium" data-testid="admin-panel-heading">
            Admin panel
          </h1>
          <MobileBookingSearch bookings={bookings} />
        </div>

        <GooglePlacesHealthBanner />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {/* Iter 69 — Collapsible top menu. Header bar always shows the
              section the admin is on; clicking the chevron toggles the full
              pill list. State is persisted in localStorage. */}
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setTabsCollapsed((c) => !c)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-indigo-50/70 via-violet-50/40 to-purple-50/60 border border-indigo-100 shadow-sm ring-1 ring-indigo-50 hover:shadow-md transition"
              data-testid="admin-tabs-collapse-toggle"
              aria-expanded={!tabsCollapsed}
              aria-controls="admin-main-tabs"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <span className="h-8 w-8 rounded-xl bg-white text-indigo-600 flex items-center justify-center ring-1 ring-indigo-200 shadow-sm shrink-0">
                  <MenuIcon className="h-4 w-4" />
                </span>
                <span className="font-bold text-sm text-indigo-900 truncate">
                  {_ADMIN_TAB_LABELS[tab] || "Menu"}
                </span>
              </span>
              <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-indigo-700 font-bold shrink-0">
                {tabsCollapsed ? "Open" : "Close"}
                {tabsCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </span>
            </button>
          </div>
          {!tabsCollapsed && (
          <TabsList className="w-full justify-start flex-wrap gap-2 h-auto bg-gradient-to-r from-indigo-50/70 via-violet-50/40 to-purple-50/60 border border-indigo-100 p-2 rounded-2xl mb-6 shadow-sm ring-1 ring-indigo-50" data-testid="admin-main-tabs">
            <TabsTrigger
              value="operations"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-indigo-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-indigo-200 hover:text-indigo-700 transition-all"
              data-testid="admin-tab-operations"
            >
              <span className="h-7 w-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center ring-1 ring-indigo-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Settings className="h-4 w-4" />
              </span>
              Platform Operations
            </TabsTrigger>
            <TabsTrigger
              value="bookings"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-blue-200 hover:text-blue-700 transition-all"
              data-testid="admin-tab-bookings"
            >
              <span className="h-7 w-7 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center ring-1 ring-blue-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <ListChecks className="h-4 w-4" />
              </span>
              Bookings
              {bookings.length > 0 && (
                <span className="ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-white/25 text-current font-bold ring-1 ring-white/40 data-[state=inactive]:bg-blue-100 data-[state=inactive]:text-blue-700">
                  {bookings.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="billing"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-emerald-200 hover:text-emerald-700 transition-all"
              data-testid="admin-tab-billing"
            >
              <span className="h-7 w-7 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Wallet className="h-4 w-4" />
              </span>
              Billing
            </TabsTrigger>
            <TabsTrigger
              value="vendors"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-amber-200 hover:text-amber-700 transition-all"
              data-testid="admin-tab-vendors"
            >
              <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center ring-1 ring-amber-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Store className="h-4 w-4" />
              </span>
              Vendors
              {attentionCounts.total > 0 ? (
                <span
                  className="ml-0.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white font-bold ring-2 ring-white/40 animate-pulse"
                  title={[
                    attentionCounts.pending_vendors ? `${attentionCounts.pending_vendors} pending approval` : null,
                    attentionCounts.pending_documents ? `${attentionCounts.pending_documents} documents to review` : null,
                    attentionCounts.new_signups_24h ? `${attentionCounts.new_signups_24h} new signup${attentionCounts.new_signups_24h === 1 ? "" : "s"} in last 24h` : null,
                  ].filter(Boolean).join(" · ")}
                  data-testid="admin-vendors-attention-badge"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  {attentionCounts.total}
                </span>
              ) : vendors.length > 0 && (
                <span className="ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-white/25 text-current font-bold ring-1 ring-white/40 data-[state=inactive]:bg-amber-100 data-[state=inactive]:text-amber-700">
                  {vendors.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="site"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-amber-200 hover:text-amber-700 transition-all"
              data-testid="admin-tab-site"
            >
              <span className="h-7 w-7 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center ring-1 ring-amber-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Globe className="h-4 w-4" />
              </span>
              Site Settings
            </TabsTrigger>
            <TabsTrigger
              value="tools"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-violet-200 hover:text-violet-700 transition-all"
              data-testid="admin-tab-tools"
            >
              <span className="h-7 w-7 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center ring-1 ring-violet-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <Wrench className="h-4 w-4" />
              </span>
              Tools
            </TabsTrigger>
            <TabsTrigger
              value="account"
              className="group rounded-xl gap-2 font-bold px-3.5 py-2 text-neutral-600 data-[state=active]:bg-gradient-to-br data-[state=active]:from-rose-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-md data-[state=active]:shadow-rose-200 hover:text-rose-700 transition-all"
              data-testid="admin-tab-account"
            >
              <span className="h-7 w-7 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center ring-1 ring-rose-200 group-data-[state=active]:bg-white/25 group-data-[state=active]:text-white group-data-[state=active]:ring-white/30 transition-colors">
                <UserCog className="h-4 w-4" />
              </span>
              My Account
            </TabsTrigger>
          </TabsList>
          )}

          {/* Iter 69 — Stats and search sit BELOW the menu, INSIDE Tabs so
              they stay between TabsList and TabsContent. They give whole-
              panel context regardless of which tab is active. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { icon: Users, label: "Total users", val: stats?.total_users ?? 0, tone: "from-violet-500 to-purple-600", text: "text-violet-600" },
              { icon: Store, label: "Vendors", val: stats?.total_vendors ?? 0, tone: "from-amber-500 to-orange-500", text: "text-amber-600" },
              { icon: CarIcon, label: "Cars listed", val: stats?.total_cars ?? 0, tone: "from-sky-500 to-blue-600", text: "text-sky-600" },
              { icon: CalendarIcon, label: "Bookings", val: stats?.total_bookings ?? 0, tone: "from-blue-500 to-blue-600", text: "text-blue-600" },
            ].map((s) => (
              <div key={s.label} className="p-5 rounded-2xl border border-border bg-white relative overflow-hidden hover:shadow-md transition-shadow" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className={`absolute top-0 right-0 w-20 h-20 rounded-full bg-gradient-to-br ${s.tone} opacity-10 -translate-y-6 translate-x-6 pointer-events-none`} />
                <s.icon className={`h-5 w-5 ${s.text} mb-2`} />
                <div className="text-2xl sm:text-3xl font-display font-bold">{s.val}</div>
                <div className="text-sm text-neutral-500 mt-1 font-semibold">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search vendors, bookings by code, or vehicles…"
                className="pl-11 pr-10 h-12 rounded-full border-border bg-white text-sm"
                data-testid="admin-search-input"
              />
              {hasQuery && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
                  data-testid="admin-search-clear"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {hasQuery && (
              <div className="text-xs text-neutral-500 mt-2 pl-2" data-testid="admin-search-summary">
                {filteredVendors.length} vendor{filteredVendors.length === 1 ? "" : "s"} · {filteredBookings.length} booking{filteredBookings.length === 1 ? "" : "s"} matching &quot;{query}&quot;
              </div>
            )}
          </div>
          {/* Platform Operations */}
          <TabsContent value="operations" className="mt-0" data-testid="admin-content-operations">
            {/* Iter 96 — clickable daily KPI cards with All Vendors filter. */}
            <OpsSummaryCards role="admin" vendors={vendors} onNavigate={handleSummaryNav} />
            <AdminOpsPanel />
            <div className="mt-6">
              <AdminChatLeadsTile />
            </div>
            <div className="mt-6">
              <AdminChatAnalyticsTile />
            </div>
            <div className="mt-6">
              <AdminSearchConsoleHealthTile />
            </div>
            <div className="mt-6">
              <AdminFaqsPanel />
            </div>
          </TabsContent>

          {/* Bookings — sub-tabbed: All bookings · Live calendar */}
          <TabsContent value="bookings" className="mt-0" data-testid="admin-content-bookings">
            <Tabs value={bookingsSubtab} onValueChange={setBookingsSubtab} className="w-full">
              <div className="flex items-baseline justify-between mb-6 gap-3 flex-wrap">
                <TabsList className="bg-blue-50 p-1 gap-1 rounded-full border border-blue-100" data-testid="admin-bookings-subtabs">
                  <TabsTrigger value="list" className="rounded-full gap-1.5 font-semibold px-4 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="admin-bookings-subtab-list">
                    <ListChecks className="h-3.5 w-3.5" /> All Bookings
                  </TabsTrigger>
                  <TabsTrigger value="calendar" className="rounded-full gap-1.5 font-semibold px-4 text-blue-700 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm" data-testid="admin-bookings-subtab-calendar">
                    <CalendarRange className="h-3.5 w-3.5" /> Live Bookings Calendar
                  </TabsTrigger>
                </TabsList>
                {/* Iter 76 — quick day filters, all vendors (Albania time). */}
                <TodayBookingFilters bookings={bookings} active={todayFilter} onChange={setTodayFilter} />
                <ManualBookingDialog onCreated={loadBookings} />
              </div>

              <TabsContent value="list" className="mt-0">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-neutral-500" data-testid="admin-bookings-count-label">Latest first · {displayedBookings.length} of {bookings.length}</span>
                </div>
                {/* Iter 89 — pending host approvals (admin can act on behalf). */}
                <PendingApprovalsPanel bookings={bookings} scope="admin" onChanged={loadBookings} />
                <div data-testid="admin-bookings-list">
                  <VendorBookingsList
                    bookings={displayedBookings}
                    depositLoadingId={null}
                    onRelease={() => toast.info("Use the deposit panel — admin actions are routed through the vendor flow.")}
                    onWithhold={() => toast.info("Use the deposit panel — admin actions are routed through the vendor flow.")}
                    onChanged={loadBookings}
                  />
                </div>
              </TabsContent>

              <TabsContent value="calendar" className="mt-0" data-testid="admin-bookings-calendar-wrap">
                <BookingsCalendar scope="admin" />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Billing — subscriptions + platform invoices (vendor bills) in one place */}
          <TabsContent value="billing" className="mt-0" data-testid="admin-content-billing">
            <div className="space-y-10">
              {/* Iter 101 — daily payments & commissions overview. */}
              <AdminFinancePanel vendors={vendors} />
              <AdminBillingPanel />
              <div className="pt-2 border-t border-border" data-testid="admin-billing-platform-section">
                <AdminPlatformInvoicesPanel />
              </div>
            </div>
          </TabsContent>

          {/* Vendors */}
          <TabsContent value="vendors" className="mt-0" data-testid="admin-content-vendors">
            <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
              <h2 className="font-display text-2xl font-medium">Vendors</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <AdminRentalCarImportDialog />
                {(hasQuery || hasActiveVendorFilter) && (
                  <span className="text-xs text-neutral-500" data-testid="admin-vendors-count">
                    {filteredVendors.length} of {vendors.length}
                  </span>
                )}
              </div>
            </div>

            <div className="mb-4">
              <AdminVendorFilters
                vendors={vendors}
                filters={vendorFilters}
                onChange={setVendorFilters}
              />
            </div>

            {attentionCounts.total > 0 && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3 text-sm text-amber-900" data-testid="admin-vendors-attention-banner">
                <span className="font-semibold uppercase tracking-wider text-[10px] bg-amber-200 text-amber-900 rounded-full px-2 py-0.5">Needs attention</span>
                {attentionCounts.pending_vendors > 0 && (
                  <span><strong>{attentionCounts.pending_vendors}</strong> pending approval</span>
                )}
                {attentionCounts.pending_documents > 0 && (
                  <span><strong>{attentionCounts.pending_documents}</strong> document{attentionCounts.pending_documents === 1 ? "" : "s"} awaiting review</span>
                )}
                {attentionCounts.new_signups_24h > 0 && (
                  <span><strong>{attentionCounts.new_signups_24h}</strong> new signup{attentionCounts.new_signups_24h === 1 ? "" : "s"} in last 24h</span>
                )}
              </div>
            )}

            {/* Mobile + tablet: stacked card list. Desktop (md+) gets the
                original table. Single source of truth for the row content. */}
            <div className="md:hidden space-y-3" data-testid="admin-vendors-cards">
              {filteredVendors.map((v) => (
                <div key={v.id} className="rounded-2xl border border-border bg-white p-4 space-y-3" data-testid={`vendor-card-${v.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{v.name}</div>
                      <div className="text-xs text-neutral-500 truncate">{v.email}</div>
                      {v.business_name && <div className="text-xs text-neutral-700 mt-0.5 truncate">{v.business_name}</div>}
                    </div>
                    <Badge
                      variant={v.vendor_status === "approved" ? "default" : v.vendor_status === "rejected" ? "destructive" : "secondary"}
                      className={`flex-shrink-0 ${v.vendor_status === "approved" ? "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent" : ""}`}
                    >
                      {v.vendor_status}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCommissionTarget(v)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        (v.commission_mode && v.commission_mode !== "default")
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-border text-neutral-600"
                      }`}
                    >
                      {commissionLabel(v)}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1 border-t border-border">
                    <Button size="sm" variant="ghost" onClick={() => impersonate(v.id, "vendor").catch(() => toast.error("Could not open vendor dashboard"))} className="rounded-full text-xs text-rose-700" data-testid={`vendor-open-dashboard-${v.id}`}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Dashboard
                    </Button>
                    <VendorAccessTrigger vendor={v} />
                    <Button size="sm" variant="ghost" onClick={() => setDocsTarget(v)} className="rounded-full text-xs text-indigo-700">
                      <FileTextIcon className="h-3.5 w-3.5 mr-1" /> Docs
                      {v.documents_approved === false && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleManual(v)} className={`rounded-full text-xs ${v.manual_booking_enabled ? "text-emerald-700" : "text-neutral-500"}`}>
                      {v.manual_booking_enabled ? "Manual ✓" : "Manual ✕"}
                    </Button>
                    {v.vendor_status !== "approved" && <Button size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" onClick={() => approve(v.id)}>Approve</Button>}
                    {v.vendor_status !== "rejected" && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => reject(v.id)}>Reject</Button>}
                  </div>
                </div>
              ))}
              {filteredVendors.length === 0 && (
                <div className="rounded-2xl border border-border bg-white px-5 py-12 text-center text-neutral-500">
                  {hasQuery || hasActiveVendorFilter
                    ? "No vendors match the current filters."
                    : "No vendors yet."}
                </div>
              )}
            </div>

            <div className="hidden md:block rounded-2xl border border-border overflow-hidden bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 text-left">
                    <tr>
                      <th className="px-5 py-3 font-medium">Name</th>
                      <th className="px-5 py-3 font-medium hidden lg:table-cell">Email</th>
                      <th className="px-5 py-3 font-medium hidden xl:table-cell">Business</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium hidden lg:table-cell">Commission</th>
                      <th className="px-5 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((v) => (
                      <tr key={v.id} className="border-t border-border" data-testid={`vendor-row-${v.id}`}>
                        <td className="px-5 py-4">
                          <div className="font-medium">{v.name}</div>
                          <div className="text-xs text-neutral-500 lg:hidden">{v.email}</div>
                        </td>
                        <td className="px-5 py-4 text-neutral-600 hidden lg:table-cell">{v.email}</td>
                        <td className="px-5 py-4 text-neutral-600 hidden xl:table-cell">{v.business_name || "—"}</td>
                        <td className="px-5 py-4">
                          <Badge
                            variant={v.vendor_status === "approved" ? "default" : v.vendor_status === "rejected" ? "destructive" : "secondary"}
                            className={v.vendor_status === "approved" ? "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent" : ""}
                          >
                            {v.vendor_status}
                          </Badge>
                        </td>
                        <td className="px-5 py-4 hidden lg:table-cell">
                          <button
                            type="button"
                            onClick={() => setCommissionTarget(v)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition ${
                              (v.commission_mode && v.commission_mode !== "default")
                                ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                : "border-border text-neutral-600 hover:bg-neutral-50"
                            }`}
                            data-testid={`commission-${v.id}`}
                            title="Edit per-vendor commission"
                          >
                            {commissionLabel(v)}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-right space-x-2 whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => impersonate(v.id, "vendor").catch(() => toast.error("Could not open vendor dashboard"))} className="rounded-full text-xs text-rose-700" data-testid={`vendor-open-dashboard-${v.id}`}>
                            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open Dashboard
                          </Button>
                          <VendorAccessTrigger vendor={v} />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDocsTarget(v)}
                            className="rounded-full text-xs text-indigo-700 hover:text-indigo-800"
                            data-testid={`docs-${v.id}`}
                            title="Review documents & contracts"
                          >
                            <FileTextIcon className="h-3.5 w-3.5 mr-1" />
                            Docs
                            {v.documents_approved === false && (
                              <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleManual(v)}
                            className={`rounded-full text-xs ${v.manual_booking_enabled ? "text-emerald-700 hover:text-emerald-800" : "text-neutral-500"}`}
                            data-testid={`toggle-manual-${v.id}`}
                            title="Toggle manual booking access for this vendor"
                          >
                            {v.manual_booking_enabled ? "Manual ✓" : "Manual ✕"}
                          </Button>
                          {v.vendor_status !== "approved" && <Button size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" onClick={() => approve(v.id)} data-testid={`approve-${v.id}`}>Approve</Button>}
                          {v.vendor_status !== "rejected" && <Button size="sm" variant="ghost" className="rounded-full" onClick={() => reject(v.id)} data-testid={`reject-${v.id}`}>Reject</Button>}
                        </td>
                      </tr>
                    ))}
                    {filteredVendors.length === 0 && (
                      <tr><td colSpan={6} className="px-5 py-12 text-center text-neutral-500">
                        {hasQuery || hasActiveVendorFilter
                          ? "No vendors match the current filters."
                          : "No vendors yet."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Site Settings — social URLs + Terms + Privacy content */}
          <TabsContent value="site" className="mt-0" data-testid="admin-content-site">
            <div className="space-y-12">
              <AdminSiteSettingsPanel />
              {/* Iter 67ak — same Site Settings tab also exposes the Twilio +
                  Google Places credential management. Keeping the two in one
                  tab avoids the "tab proliferation" anti-pattern; the visual
                  hierarchy (h3 + colour-coded icons) makes the boundary clear. */}
              <div className="border-t border-border pt-12" data-testid="admin-credentials-section">
                <header className="mb-6">
                  <h2 className="font-display text-3xl font-semibold tracking-tight">Integrations</h2>
                  <p className="text-sm text-neutral-500 mt-1.5">Saved credentials override <code className="font-mono text-xs bg-neutral-100 px-1 rounded">backend/.env</code> — clearing a field falls back to the env value or disables the integration.</p>
                </header>
                <AdminIntegrationCredentialsPanel />
              </div>
            </div>
          </TabsContent>

          {/* My Account */}
          <TabsContent value="tools" className="mt-0" data-testid="admin-content-tools">
            <header className="mb-6">
              <h2 className="font-display text-3xl font-semibold tracking-tight">Tools</h2>
              <p className="text-sm text-neutral-500 mt-1.5">Internal debugging surfaces — no DB writes, safe to use during vendor-support calls.</p>
            </header>
            <AdminPricingPlayground />
            <div className="mt-6">
              <AdminFxPanel />
            </div>
            <div className="mt-6">
              <AdminFeaturedPanel />
            </div>
            <div className="mt-6">
              <AdminImpersonationLogPanel />
            </div>
          </TabsContent>

          {/* My Account */}
          <TabsContent value="account" className="mt-0" data-testid="admin-content-account">
            <h2 className="font-display text-2xl font-medium mb-6">My account</h2>
            <AdminCredentialsPanel />
            <div className="mt-8">
              <AdminTeamPanel />
            </div>
            {/* Iter 74t — AI Settings lives under Admin → My account → below
                Team so it stays visible only to authenticated admins and
                never appears on a public tab. Provides encrypted API-key
                storage for OpenAI / Gemini / Anthropic / Emergent. */}
            <div className="mt-12 pt-8 border-t border-neutral-200">
              <AISettingsPanel />
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <VendorCommissionDialog
        vendor={commissionTarget}
        open={!!commissionTarget}
        onClose={() => setCommissionTarget(null)}
        onSaved={load}
      />
      <AdminVendorDocumentsDialog
        vendor={docsTarget}
        open={!!docsTarget}
        onOpenChange={(o) => !o && setDocsTarget(null)}
        onChanged={load}
      />
      {/* Iter 96 — mobile bottom nav + floating quick actions. */}
      <QuickActionsFab actions={fabActions} />
      <OpsBottomNav role="admin" active={bottomNavActive} onNavigate={handleBottomNav} />
      <div className="h-16 md:hidden" />
      <Footer />
      {/* Iter 68g — Live admin chat console floating popup. */}
      <AdminLiveChatPopup />
    </div>
  );
}
